import { FolderContent } from '@/api/generatedApi';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { Thumbnail } from '@/components/Thumbnail';
import { AddToCompareButton } from '@/components/CompareCart/AddToCompareButton';
import { FindMatchesButton } from '@/components/Matches/FindMatchesButton';
import { DownloadAssetButton } from '@/components/AssetActionRow/DownloadAssetButton';
import { DeleteAssetButton } from '@/components/AssetActionRow/DeleteAssetButton';
import { getAssetName } from '@/utils/assets';
import { formatMatchPercentage, pluralize } from '@/utils/format';
import { MediaArea, StyledCard, StyledCardContent, InlineLink } from '@/components/CardGrid/cardStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { getFileExtension } from '@physna/supported-files';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { ReportBreadcrumbToolbar } from './ReportBreadcrumbToolbar';
import {
  NO_VISIBLE_MATCH_LABEL,
  NO_VISIBLE_MATCH_MARK,
  getClosestMatch,
  getClosestMatchDescription,
  getMatchCount,
  orderByClosestMatch,
} from './groupAssetMatches';

const CARD_WIDTH = 240;
const CARD_HEIGHT = 316;

const THUMBNAIL_SIZE = 160;

const actionRowSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0.5,
  '& .MuiIconButton-root': { padding: '6px' },
  '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
};

// matchPercentage is undefined when the asset has no match to show — filtered by permission,
// moved into the group, or left alone in it. The pill still renders so cards keep one shape,
// but it shows a mark, since none of those cases is a measured zero.
const MatchPill = ({ matchPercentage, description }: { matchPercentage?: number; description: string }) => (
  <Tooltip title={description} placement='left'>
    <Chip
      label={matchPercentage === undefined ? NO_VISIBLE_MATCH_MARK : `${formatMatchPercentage(matchPercentage)}%`}
      size='small'
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        fontWeight: 600,
        zIndex: 1,
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      }}
    />
  </Tooltip>
);

const GroupAssetCard = ({
  asset,
  onContextMenu,
}: {
  asset: ReportGroupAsset;
  onContextMenu?: (event: React.MouseEvent) => void;
}) => {
  const { getPath } = useTenants();
  const extension = getFileExtension(asset.path);
  const assetPath = getPath(`asset/${asset.id}`);
  const closestMatch = getClosestMatch(asset);

  // A bare percentage with nothing to read it against is the confusion this view
  // exists to remove, so the caption always names the asset it is measured to.
  // Asset names run long enough to ellipsize, so the count gets its own line
  // rather than being truncated away with the tail of the name.
  const closestLabel = closestMatch ? `closest: ${getAssetName(closestMatch.asset)}` : NO_VISIBLE_MATCH_LABEL;

  return (
    <Box
      onContextMenu={onContextMenu}
      sx={{ position: 'relative', width: CARD_WIDTH, height: CARD_HEIGHT, flexShrink: 0 }}
    >
      <StyledCard sx={{ cursor: 'default' }}>
        <MediaArea>
          <MatchPill matchPercentage={closestMatch?.matchPercentage} description={getClosestMatchDescription(asset)} />
          <Thumbnail asset={asset} size={THUMBNAIL_SIZE} />
        </MediaArea>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <InlineLink to={assetPath} draggable={false} sx={{ display: 'block', overflow: 'hidden', minWidth: 0 }}>
              <Typography
                variant='body2'
                component='span'
                sx={{
                  fontWeight: 600,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getAssetName(asset)}
              </Typography>
            </InlineLink>
            {extension && <Chip label={extension} size='small' variant='outlined' sx={{ flexShrink: 0 }} />}
          </Box>
          <Typography
            variant='caption'
            title={closestLabel}
            sx={{
              display: 'block',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {closestLabel}
          </Typography>
          {closestMatch && (
            <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
              {pluralize(getMatchCount(asset), 'match', 'matches')}
            </Typography>
          )}
        </StyledCardContent>
        <Box sx={actionRowSx}>
          <FindMatchesButton asset={asset} variant='icon' />
          <AddToCompareButton asset={asset} variant='icon' quickCompare={closestMatch?.asset.id} />
          <DownloadAssetButton asset={asset} variant='icon' />
          <DeleteAssetButton asset={asset} variant='icon' />
        </Box>
      </StyledCard>
    </Box>
  );
};

export const ReportMatchListView = ({
  assets,
  groupName,
  isLoading,
  toolbarActions,
  onCardContextMenu,
}: {
  assets: ReportGroupAsset[];
  groupName: string;
  isLoading: boolean;
  toolbarActions?: React.ReactNode;
  onCardContextMenu?: (asset: FolderContent, event: React.MouseEvent) => void;
}) => {
  const groupAssets = orderByClosestMatch(assets);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <ReportBreadcrumbToolbar groupName={groupName} toolbarActions={toolbarActions} />
      <Box sx={{ height: '100%', minHeight: 0, overflowY: 'auto', backgroundColor: 'background.paper' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && groupAssets.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, height: '100%' }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              No assets in this group.
            </Typography>
          </Box>
        )}
        {!isLoading && groupAssets.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              padding: 2,
            }}
          >
            {groupAssets.map(asset => (
              <GroupAssetCard
                key={asset.id}
                asset={asset}
                onContextMenu={
                  onCardContextMenu
                    ? event => {
                        event.preventDefault();
                        onCardContextMenu({ ...asset, contentType: 'asset' as const }, event);
                      }
                    : undefined
                }
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
